import { isLiteralType, toLiteralType } from '@util/types';

export enum ValidSport {
    BASEBALL = 'baseball',
    BASKETBALL = 'basketball',
    BOWLING = 'bowling',
    CHEERLEADING = 'cheerleading',
    CROSS_COUNTY = 'crossCountry',
    FIELD_HOCKEY = 'fieldHockey',
    FLAG_FOOTBALL = 'flagFootball',
    FOOTBALL = 'football',
    GOLF = 'golf',
    HOCKEY = 'hockey',
    LACROSSE = 'lacrosse',
    ROWING_AND_CREW = 'rowingAndCrew',
    RUGBY = 'rugby',
    SOCCER = 'soccer',
    SOFTBALL = 'softball',
    SWIMMING_AND_DIVING = 'swimmingAndDiving',
    TENNIS = 'tennis',
    TRACK_AND_FIELD = 'trackAndField',
    VOLLEYBALL = 'volleyball',
    WATER_POLO = 'waterPolo',
    WRESTLING = 'wrestling',
}

export enum ValidOpponentSport {
    BASEBALL = 'baseballOpponent',
    BASKETBALL = 'basketballOpponent',
    BOWLING = 'bowlingOpponent',
    CHEERLEADING = 'cheerleadingOpponent',
    CROSS_COUNTY = 'crossCountryOpponent',
    FIELD_HOCKEY = 'fieldHockeyOpponent',
    FLAG_FOOTBALL = 'flagFootballOpponent',
    FOOTBALL = 'footballOpponent',
    GOLF = 'golfOpponent',
    HOCKEY = 'hockeyOpponent',
    LACROSSE = 'lacrosseOpponent',
    ROWING_AND_CREW = 'rowingAndCrewOpponent',
    RUGBY = 'rugbyOpponent',
    SOCCER = 'soccerOpponent',
    SOFTBALL = 'softballOpponent',
    SWIMMING_AND_DIVING = 'swimmingAndDivingOpponent',
    TENNIS = 'tennisOpponent',
    TRACK_AND_FIELD = 'trackAndFieldOpponent',
    VOLLEYBALL = 'volleyballOpponent',
    WATER_POLO = 'waterPoloOpponent',
    WRESTLING = 'wrestlingOpponent',
}

export enum InvalidSport {
    OTHER = 'other',
}

export enum InvalidOpponentSport {
    OTHER = 'otherOpponent',
}

export const Sport = { ...InvalidSport, ...ValidSport };
export const OpponentSport = { ...InvalidOpponentSport, ...ValidOpponentSport };

export type Sport = InvalidSport | ValidSport;
export type OpponentSport = InvalidOpponentSport | ValidOpponentSport;

export const isSport = (str: string): str is Sport => isLiteralType(str, Object.values(Sport));
export const toSport = (str: string) => toLiteralType(str, 'Sport', Object.values(Sport));
export const toValidSport = (str: string) => toLiteralType(str, 'ValidSport', Object.values(ValidSport));

export const formatSport = (sport: Sport): string => {
    switch (sport) {
        case Sport.BASEBALL:
            return 'Baseball';
        case Sport.BASKETBALL:
            return 'Basketball';
        case Sport.BOWLING:
            return 'Bowling';
        case Sport.CHEERLEADING:
            return 'Cheerleading';
        case Sport.ROWING_AND_CREW:
            return 'Crew & rowing';
        case Sport.CROSS_COUNTY:
            return 'Cross country';
        case Sport.FIELD_HOCKEY:
            return 'Field hockey';
        case Sport.FLAG_FOOTBALL:
            return 'Flag football';
        case Sport.FOOTBALL:
            return 'Football';
        case Sport.GOLF:
            return 'Golf';
        case Sport.HOCKEY:
            return 'Hockey';
        case Sport.LACROSSE:
            return 'Lacrosse';
        case Sport.RUGBY:
            return 'Rugby';
        case Sport.SOCCER:
            return 'Soccer';
        case Sport.SOFTBALL:
            return 'Softball';
        case Sport.SWIMMING_AND_DIVING:
            return 'Swimming & diving';
        case Sport.TENNIS:
            return 'Tennis';
        case Sport.TRACK_AND_FIELD:
            return 'Track & field';
        case Sport.VOLLEYBALL:
            return 'Volleyball';
        case Sport.WATER_POLO:
            return 'Water polo';
        case Sport.WRESTLING:
            return 'Wrestling';
        case Sport.OTHER:
            return '';
        default:
            throw new Error(`Unknown sport, ${sport}`);
    }
};

const STATS_ENABLED_SPORTS: Sport[] = [Sport.BASEBALL, Sport.SOFTBALL, Sport.BASKETBALL];
export function sportHasStatsEnabled(sport: Sport | undefined) {
    if (sport === undefined) return false;
    return STATS_ENABLED_SPORTS.includes(sport);
}

const BAT_SPORTS: Sport[] = [Sport.BASEBALL, Sport.SOFTBALL];
export function sportIsBaseballOrSoftball(
    sport: Sport | undefined,
): sport is typeof Sport.BASEBALL | typeof Sport.SOFTBALL {
    return sport !== undefined && BAT_SPORTS.includes(sport);
}

const FOOTBALL_SPORTS: Sport[] = [Sport.FOOTBALL, Sport.FLAG_FOOTBALL];
export function sportIsFootballOrFlagfootball(
    sport: Sport | undefined,
): sport is typeof Sport.FOOTBALL | typeof Sport.FLAG_FOOTBALL {
    return sport !== undefined && FOOTBALL_SPORTS.includes(sport);
}

export function sportIsValid(sport: Sport): sport is ValidSport {
    return Object.values(ValidSport).includes(sport as ValidSport);
}

export const SPORTS_WITH_DELETE_CONFIRMATION: Sport[] = [Sport.BASEBALL, Sport.SOFTBALL, Sport.BASKETBALL];
export function sportHasDeleteConfirmation(sport: Sport): boolean {
    return SPORTS_WITH_DELETE_CONFIRMATION.includes(sport);
}
